/* ============================================================================
   ATE SCROLL COVER SLIDER — instance-scoped custom element. No libraries.
   The section is taller than the viewport (one viewport + one step per extra
   slide); the stage is position:sticky and fills the screen, and the active
   slide is derived from scroll progress — scrolling advances the categories one
   by one before the page continues to the next section. rAF-throttled with a
   setTimeout fallback (some embedded previews starve rAF). Hover preview on fine
   pointers; touch = first tap selects, second tap opens the collection. Never
   locks body scroll.
   ============================================================================ */
(function () {
  'use strict';
  if (customElements.get('ate-cover-story')) { return; }

  var HOVER_FINE = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : { matches: false };

  customElements.define('ate-cover-story', class extends HTMLElement {
    connectedCallback() {
      if (this._inited) { return; }
      this._inited = true;

      this.slides = Array.prototype.slice.call(this.querySelectorAll('.ate-cover-story__slide'));
      this.navLinks = Array.prototype.slice.call(this.querySelectorAll('.ate-cover-story__nav-link'));
      this.progressCurrent = this.querySelector('[data-progress-current]');
      this.cta = this.querySelector('[data-cover-cta]');
      this.count = this.slides.length;
      this.activeIndex = 0;
      this._tick = false;

      var self = this;
      this._onScroll = this._schedule.bind(this);
      this._onBlockSelect = this._blockSelect.bind(this);

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll);
      window.addEventListener('orientationchange', this._onScroll);
      document.addEventListener('shopify:block:select', this._onBlockSelect);

      // desktop hover preview (fine pointers only)
      if (HOVER_FINE.matches) {
        this.navLinks.forEach(function (link, i) {
          link.addEventListener('mouseenter', function () { self._setActive(i); });
        });
      }

      // Touch behaviour: first tap on a category only SELECTS it (scrolls it into the active
      // position); a second tap on the already-active category opens its collection page. On
      // fine pointers (desktop) a single click navigates as usual (hover already previews).
      this.navLinks.forEach(function (link, i) {
        link.addEventListener('click', function (e) {
          if (HOVER_FINE.matches) { return; }
          if (self.activeIndex !== i) {
            e.preventDefault();
            self._scrollToIndex(i);
          }
        });
      });

      var boot = function () { self._update(); };
      if (document.readyState !== 'loading') { setTimeout(boot, 80); }
      else { document.addEventListener('DOMContentLoaded', function () { setTimeout(boot, 80); }); }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
      window.removeEventListener('orientationchange', this._onScroll);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      this._inited = false;
    }

    /* rAF throttle + timeout fallback: whichever fires first runs one update */
    _schedule() {
      if (this._tick) { return; }
      this._tick = true;
      var self = this;
      var done = false;
      var run = function () {
        if (done) { return; }
        done = true;
        self._tick = false;
        self._update();
      };
      requestAnimationFrame(run);
      setTimeout(run, 60);
    }

    _update() {
      // keep the sticky stage covering the full visible viewport (kills the gap that svh/dvh
      // mismatches create in iOS Safari / app-preview webviews)
      var vh = window.innerHeight;
      if (vh && vh !== this._lastVh) {
        this._lastVh = vh;
        this.style.setProperty('--ate-stage-h', vh + 'px');
      }
      if (this.count < 2) { return; }
      var rect = this.getBoundingClientRect();
      var scrollable = this.offsetHeight - window.innerHeight;
      if (scrollable <= 0) { return; }
      var travelled = Math.min(Math.max(-rect.top, 0), scrollable);
      var progress = travelled / scrollable;
      var p = progress * (this.count - 1); // fractional position across the slides
      this._applyTransforms(p);
      this.style.setProperty('--ate-progress', String(progress)); // smooth marker
      this._setActive(Math.round(p)); // nav highlight snaps to nearest category
    }

    /* continuous scrub: each slide's vertical offset tracks scroll 1:1, so two images slide
       gradually past each other (they tile exactly — outgoing goes up, incoming rises from below) */
    _applyTransforms(p) {
      for (var k = 0; k < this.count; k++) {
        var y = (k - p) * 100;
        if (y > 100) { y = 100; } else if (y < -100) { y = -100; }
        this.slides[k].style.setProperty('--slide-y', y + '%');
      }
    }

    _setActive(index) {
      index = Math.min(Math.max(index, 0), this.count - 1);
      if (index === this.activeIndex && this._painted) { return; }
      this._painted = true;
      this.activeIndex = index;

      this.navLinks.forEach(function (link, i) {
        var active = i === index;
        link.classList.toggle('is-active', active);
        if (active) { link.setAttribute('aria-current', 'true'); }
        else { link.removeAttribute('aria-current'); }
      });

      if (this.progressCurrent) { this.progressCurrent.textContent = String(index + 1); }

      // CTA follows the active slide when the section is set to "active" mode
      if (this.cta && this.getAttribute('data-cta-mode') === 'active') {
        var url = this.slides[index].getAttribute('data-slide-url');
        if (url && url !== '#') { this.cta.setAttribute('href', url); }
      }

      this._preload(index);
    }

    /* smooth-scroll the page so category `index` becomes the scroll-derived active slide */
    _scrollToIndex(index) {
      index = Math.min(Math.max(index, 0), this.count - 1);
      var scrollable = this.offsetHeight - window.innerHeight;
      if (scrollable > 0 && this.count > 1) {
        var top = this.getBoundingClientRect().top + window.pageYOffset;
        var target = top + (index / (this.count - 1)) * scrollable;
        window.scrollTo({ top: Math.round(target), behavior: 'smooth' });
      }
      this._setActive(index); // instant nav feedback while the smooth scroll settles
    }

    /* make the active slide's neighbours load before they are needed */
    _preload(index) {
      var self = this;
      [index, index - 1, index + 1].forEach(function (i) {
        if (i < 0 || i >= self.count) { return; }
        var img = self.slides[i].querySelector('img[loading="lazy"]');
        if (img) { img.loading = 'eager'; }
      });
    }

    /* theme editor: selecting a block activates its slide */
    _blockSelect(e) {
      if (!this.contains(e.target)) { return; }
      var idx = this.slides.indexOf(e.target.closest('.ate-cover-story__slide'));
      if (idx > -1) { this._setActive(idx); }
    }
  });
})();
